import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  filterResearchMarkets,
  formatCompactPopulation,
  getResearchMarketLatestRuleRatio,
  rankResearchMarkets,
  researchMarketPopulationTitle,
  researchMarketPopulationTone,
} from '../lib/researchMarkets';
import FemaNriScreeningMapSection from './FemaNriScreeningMapSection';
import { buildRegionZctaGeometry } from '../lib/usCountyMapGeometry';
import { zonesInRegion } from '../lib/sourcingZoneShape';
import { apiJson } from '../services/api';
import '../pages/Research.css';

let cachedRegionsPayload = null;
let pendingRegionsRequest = null;
const regionGeographyCache = new Map();
const pendingRegionGeographyRequests = new Map();
const REGION_GEOGRAPHY_CACHE_LIMIT = 12;

export const getCachedResearchRegionsPayload = () => cachedRegionsPayload;

export function loadResearchRegionsPayload(loader) {
  if (cachedRegionsPayload) return Promise.resolve(cachedRegionsPayload);
  if (!pendingRegionsRequest) {
    pendingRegionsRequest = loader('/research/fema-nri-map/us?hazard=overall')
      .then((payload) => {
        cachedRegionsPayload = payload || null;
        return cachedRegionsPayload;
      })
      .finally(() => { pendingRegionsRequest = null; });
  }
  return pendingRegionsRequest;
}

function regionGeographyRequest(market) {
  const stateAbbr = String(market?.state_name || '').toUpperCase();
  const countyFips = (market?.market_county_fips?.length
    ? market.market_county_fips
    : [market?.marker_county_fips]).filter(Boolean).map(String).sort();
  if (!market?.region_slug || !stateAbbr || !countyFips.length) return null;
  return {
    key: `${stateAbbr}:${countyFips.join(',')}:postal-v1`,
    url: `/research/geography/states/${stateAbbr}/region-zctas?counties=${encodeURIComponent(countyFips.join(','))}&labels=postal-v1`,
  };
}

export function loadRegionGeography(market, loader = apiJson) {
  const request = regionGeographyRequest(market);
  if (!request) return Promise.reject(new Error('Region geography is unavailable.'));
  if (regionGeographyCache.has(request.key)) {
    const payload = regionGeographyCache.get(request.key);
    regionGeographyCache.delete(request.key);
    regionGeographyCache.set(request.key, payload);
    return Promise.resolve(payload);
  }
  if (!pendingRegionGeographyRequests.has(request.key)) {
    pendingRegionGeographyRequests.set(request.key, loader(request.url)
      .then((payload) => {
        if (!Array.isArray(payload?.features) || !payload.features.length) {
          throw new Error(`ZIP-area boundaries have not been loaded for ${String(market.state_name).toUpperCase()}.`);
        }
        regionGeographyCache.set(request.key, payload);
        while (regionGeographyCache.size > REGION_GEOGRAPHY_CACHE_LIMIT) {
          regionGeographyCache.delete(regionGeographyCache.keys().next().value);
        }
        return payload;
      })
      .finally(() => pendingRegionGeographyRequests.delete(request.key)));
  }
  return pendingRegionGeographyRequests.get(request.key);
}

const WHOLE_CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
});
const PERCENT_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2,
});

function formatCompactHomePrice(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `$${Math.round(numeric / 1000)}K` : '—';
}

function formatWholeCurrency(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? WHOLE_CURRENCY_FORMATTER.format(numeric) : '—';
}

function formatPercent(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? PERCENT_FORMATTER.format(numeric) : '—';
}

function marketHomePrice(market) {
  const direct = market?.home_prices == null ? null : Number(market.home_prices);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const rent = Number(market?.screening_rent);
  const ratio = Number(market?.screening_rent_to_price_ratio ?? market?.rent_to_price_ratio_latest);
  return Number.isFinite(rent) && Number.isFinite(ratio) && ratio > 0 ? rent / ratio : null;
}

export function filterMarketsByState(markets, stateAbbreviation) {
  if (!stateAbbreviation) return markets;
  const normalizedState = String(stateAbbreviation).toUpperCase();
  return markets.filter((market) => String(market.state_name || '').toUpperCase() === normalizedState);
}

export function searchTargetKey(target) {
  if (target?.type === 'fema_county') return `fema_county:${target.fips}`;
  if (target?.type === 'zcta') return `zcta:${target.state_abbr}:${target.zcta5 || target.id}`;
  return `${target?.type}:${target?.id}`;
}

export function CompactZctaIndex({
  zctas,
  selectedTargetKeys,
  onTargetToggle,
  selectionLimit,
  selectedTargetCount,
  selectionPurpose,
  highlightedZcta = null,
  pinnedZcta = null,
  onRowHover,
  onRowFocus,
  zones = [],
  onZoneFocus,
}) {
  const [sort, setSort] = useState({ key: 'zcta5', direction: 'asc' });
  const rowRefs = useRef(new Map());
  // A ZIP pinned on the map scrolls its row into view without stealing focus.
  useEffect(() => {
    if (pinnedZcta) rowRefs.current.get(String(pinnedZcta))?.scrollIntoView?.({ block: 'nearest' });
  }, [pinnedZcta]);
  const selectable = typeof onTargetToggle === 'function';
  const rows = useMemo(() => [...zctas].sort((left, right) => {
    const direction = sort.direction === 'asc' ? 1 : -1;
    const leftValue = sort.key === 'locality'
      ? String(left.primary_locality || '')
      : sort.key === 'population'
        ? Number(left.population_2020 ?? Number.NaN)
        : sort.key === 'housing'
          ? Number(left.housing_units_2020 ?? Number.NaN)
          : String(left.zcta5 || '');
    const rightValue = sort.key === 'locality'
      ? String(right.primary_locality || '')
      : sort.key === 'population'
        ? Number(right.population_2020 ?? Number.NaN)
        : sort.key === 'housing'
          ? Number(right.housing_units_2020 ?? Number.NaN)
          : String(right.zcta5 || '');
    if (typeof leftValue === 'number' || typeof rightValue === 'number') {
      const leftMissing = !Number.isFinite(leftValue);
      const rightMissing = !Number.isFinite(rightValue);
      if (leftMissing || rightMissing) {
        if (leftMissing && rightMissing) return 0;
        return leftMissing ? 1 : -1;
      }
      return (leftValue - rightValue) * direction;
    }
    return leftValue.localeCompare(rightValue) * direction;
  }), [sort, zctas]);
  const toggleSort = (key) => setSort((current) => ({
    key,
    direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
  }));
  const heading = (key, label, help = '') => (
    <th aria-sort={sort.key === key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => toggleSort(key)}>
        <span>{label}</span>
        {help ? <span role="tooltip" className="research-map-index__header-tip">{help}</span> : null}
      </button>
    </th>
  );

  return <div className={`research-map-index research-map-index--split research-map-index--zcta${selectable ? ' is-selectable' : ''}`}><div className="research-map-index__scroll"><table><thead><tr>
    {selectable ? <th className="research-map-index__select-heading"><span className="sr-only"></span></th> : null}
    {heading('locality', 'City', 'Names observed in retained property records; ZIP areas and city boundaries do not match exactly.')}
    {heading('zcta5', 'ZIP', 'U.S. Census ZIP Code Tabulation Area.')}
    {heading('population', 'Pop', '2020 Census population for the complete ZCTA.')}
    {heading('housing', 'Housing', '2020 Census housing units for the complete ZCTA.')}
  </tr></thead><tbody>{zones.map((zone) => (
    <tr
      key={`zone:${zone.zone_id}`}
      className="research-map-index__zone-row is-map-linked"
      tabIndex={0}
      aria-label={`Show drawn area ${zone.name} on the map`}
      onClick={() => onZoneFocus?.(zone.zone_id)}
      onKeyDown={(event) => {
        if ((event.key === 'Enter' || event.key === ' ') && event.target === event.currentTarget) {
          event.preventDefault();
          onZoneFocus?.(zone.zone_id);
        }
      }}
    >
      {selectable ? <td className="research-map-index__select-cell" onClick={(event) => event.stopPropagation()}><input type="checkbox" checked={false} disabled aria-label={`${zone.name} must be validated before searching`} title="Validate this area before it can be searched (next phase)." /></td> : null}
      <td className="research-map-index__zcta-city" title={zone.name}>{zone.name}</td>
      <td><span className={`research-map-index__zone-status is-${zone.status}`}>{zone.status === 'draft' ? 'Draft' : zone.status === 'active' ? 'Active' : 'Paused'}</span></td>
      <td>—</td>
      <td>—</td>
    </tr>
  ))}{rows.map((row) => {
    const target = {
      type: 'zcta', id: row.zcta5, zcta5: row.zcta5, state_abbr: row.state_abbr,
      name: row.primary_locality ? `${row.primary_locality} · ${row.zcta5}` : `ZIP area ${row.zcta5}`,
    };
    const key = searchTargetKey(target);
    const checked = selectedTargetKeys.has(key);
    const disabled = !checked && selectedTargetCount >= selectionLimit;
    const linked = typeof onRowFocus === 'function';
    return <tr
      key={key}
      ref={(element) => {
        if (element) rowRefs.current.set(row.zcta5, element);
        else rowRefs.current.delete(row.zcta5);
      }}
      className={`${linked ? 'is-map-linked' : ''}${highlightedZcta === row.zcta5 || pinnedZcta === row.zcta5 ? ' is-highlighted' : ''}`}
      tabIndex={linked ? 0 : undefined}
      aria-label={linked ? `Show ${target.name} on the map` : undefined}
      onMouseEnter={() => onRowHover?.(row.zcta5)}
      onMouseLeave={() => onRowHover?.(null)}
      onClick={() => onRowFocus?.(row.zcta5)}
      onKeyDown={(event) => {
        if (linked && (event.key === 'Enter' || event.key === ' ') && event.target === event.currentTarget) {
          event.preventDefault();
          onRowFocus(row.zcta5);
        }
      }}
    >
      {selectable ? <td className="research-map-index__select-cell" onClick={(event) => event.stopPropagation()}><input type="checkbox" checked={checked} disabled={disabled} onChange={() => onTargetToggle(target)} aria-label={`Select ${target.name} for ${selectionPurpose}`} /></td> : null}
      <td className="research-map-index__zcta-city">{row.primary_locality || `ZIP ${row.zcta5}`}</td>
      <td>{row.zcta5}</td>
      <td><span className="research-population-badge" title={row.population_2020 == null ? 'Population unavailable' : `${Number(row.population_2020).toLocaleString()} people · 2020 Census ZCTA`}>{formatCompactPopulation(row.population_2020)}</span></td>
      <td>{row.housing_units_2020 == null ? '—' : Number(row.housing_units_2020).toLocaleString()}</td>
    </tr>;
  })}</tbody></table></div></div>;
}

export function CompactMarketIndex({ markets, selectedMarketIds = [], onSelectedMarketIdsChange, selectionLimit = Infinity, getMarketSelectionId = (market) => market.region_id ?? market.id, isMarketSelectable = () => true, getMarketDisabledReason = () => '', selectionPurpose = 'API Lab' }) {
  const [sort, setSort] = useState({ key: 'rank', direction: 'asc' });
  const selectable = typeof onSelectedMarketIdsChange === 'function';
  const selectedKeys = useMemo(() => new Set(selectedMarketIds.map(String)), [selectedMarketIds]);
  const columns = useMemo(() => ([
    { key: 'rank', label: 'Rank', value: (market) => market.rank },
    { key: 'region_name', label: 'Metro', value: (market) => market.region_name || '' },
    { key: 'home_prices', label: 'Home $', help: 'Latest Zillow home value.', value: marketHomePrice },
    { key: 'rent_prices', label: 'Rent $', help: 'Latest monthly rent used for screening.', value: (market) => Number(market.rent_prices ?? market.screening_rent) },
    { key: 'rent_to_price_ratio_latest', label: '1% Rule', help: 'Monthly rent ÷ home value × 100.', value: getResearchMarketLatestRuleRatio },
    { key: 'zillow_pair_months', label: 'MOS', help: 'Months with Zillow home + rent.', value: (market) => Number(market.zillow_pair_months) },
    { key: 'rent_to_price_ratio_12m_avg', label: '12M AVG', help: 'Latest 12-month 1% Rule average.', value: (market) => (
      market.rent_to_price_ratio_12m_avg == null ? Number.NaN : Number(market.rent_to_price_ratio_12m_avg)
    ) },
    { key: 'population', label: 'POP', help: 'Latest governed Census ACS population estimate.', value: (market) => (
      market.population_status === 'available' && market.population != null ? Number(market.population) : Number.NaN
    ) },
  ]), []);
  const sortedMarkets = useMemo(() => {
    const column = columns.find((candidate) => candidate.key === sort.key) || columns[0];
    const direction = sort.direction === 'asc' ? 1 : -1;
    return [...markets].sort((left, right) => {
      const leftValue = column.value(left);
      const rightValue = column.value(right);
      if (typeof leftValue === 'string' || typeof rightValue === 'string') {
        return String(leftValue).localeCompare(String(rightValue)) * direction;
      }
      const leftMissing = !Number.isFinite(leftValue);
      const rightMissing = !Number.isFinite(rightValue);
      if (leftMissing || rightMissing) {
        if (leftMissing && rightMissing) return 0;
        return leftMissing ? 1 : -1;
      }
      return (leftValue - rightValue) * direction;
    });
  }, [columns, markets, sort]);

  const toggleSort = (key) => setSort((current) => ({
    key,
    direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
  }));

  const toggleMarket = (market) => {
    const marketId = getMarketSelectionId(market);
    if (marketId == null) return;
    const selected = selectedKeys.has(String(marketId));
    if (!selected && !isMarketSelectable(market)) return;
    if (!selected && selectedMarketIds.length >= selectionLimit) return;
    onSelectedMarketIdsChange(selected
      ? selectedMarketIds.filter((id) => String(id) !== String(marketId))
      : [...selectedMarketIds, marketId]);
  };

  return <div className={`research-map-index research-map-index--split${selectable ? ' is-selectable' : ''}`}><div className="research-map-index__scroll"><table><thead><tr>
    {selectable ? <th className="research-map-index__select-heading"><span className="sr-only"></span></th> : null}
    {columns.map((column) => <th key={column.key} className={`research-map-index__heading--${column.key}`} aria-sort={sort.key === column.key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><button type="button" aria-label={column.label} aria-describedby={column.help ? `research-column-help-${column.key}` : undefined} onClick={() => toggleSort(column.key)}><span>{column.label}</span>{column.help ? <span id={`research-column-help-${column.key}`} role="tooltip" className="research-map-index__header-tip">{column.help}</span> : null}</button></th>)}
  </tr></thead><tbody>{sortedMarkets.map((market) => <tr key={market.region_slug}>
    {selectable ? <td className="research-map-index__select-cell"><input type="checkbox" checked={selectedKeys.has(String(getMarketSelectionId(market)))} disabled={!selectedKeys.has(String(getMarketSelectionId(market))) && (!isMarketSelectable(market) || selectedMarketIds.length >= selectionLimit)} onChange={() => toggleMarket(market)} aria-label={isMarketSelectable(market) ? `Select ${market.region_name} for ${selectionPurpose}` : `${market.region_name}: geography review required`} title={isMarketSelectable(market) ? undefined : (getMarketDisabledReason(market) || `Geography review required before ${selectionPurpose} can run.`)} /></td> : null}
    <td>{Number.isFinite(market.rank) ? `#${market.rank}` : '—'}</td>
    <td><Link to={`/research/markets/${market.region_slug}`} title={market.region_name}>{market.region_name}</Link></td>
    <td>{formatCompactHomePrice(marketHomePrice(market))}</td>
    <td>{formatWholeCurrency(market.rent_prices ?? market.screening_rent)}</td>
    <td>{formatPercent(getResearchMarketLatestRuleRatio(market))}</td>
    <td title="Paired Zillow home-value and rent months">{Number.isFinite(Number(market.zillow_pair_months)) ? Number(market.zillow_pair_months) : '—'}</td>
    <td title={Number(market.zillow_pair_months) < 12 ? 'Needs 12 paired Zillow months' : 'Average 1% Rule ratio across the latest 12 paired Zillow months'}>{Number(market.zillow_pair_months) >= 12 ? formatPercent(market.rent_to_price_ratio_12m_avg) : '—'}</td>
    <td className="research-map-index__population-cell"><span className={`research-population-badge research-population-badge--${researchMarketPopulationTone(market.population)}`} title={researchMarketPopulationTitle(market)} aria-label={researchMarketPopulationTitle(market)}>{formatCompactPopulation(market.population)}</span></td>
  </tr>)}</tbody></table></div></div>;
}

export default function ResearchRegionsWorkspace({
  active = true,
  payload,
  loading = false,
  error = '',
  viewTabs = null,
  showSourceAudit = true,
  sourceAuditKeys,
  showTitle = true,
  headerSupplement = null,
  selectedMarketIds = [],
  onSelectedMarketIdsChange,
  getMarketSelectionId,
  isMarketSelectable,
  getMarketDisabledReason,
  selectionPurpose = 'API Lab',
  selectionLimit = Infinity,
  selectedSearchTargets = [],
  onSelectedSearchTargetsChange,
  onRunSearchTarget,
  collapsible = false,
  initiallyCollapsed = false,
}) {
  const [marketSearch, setMarketSearch] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [regionZctaPayload, setRegionZctaPayload] = useState(null);
  const [regionLoading, setRegionLoading] = useState(false);
  const [regionError, setRegionError] = useState('');
  const regionRequestRef = useRef(0);
  const [tableHoverZcta, setTableHoverZcta] = useState(null);
  const [zctaFocusRequest, setZctaFocusRequest] = useState(null);
  const [mapZctaState, setMapZctaState] = useState({ activeId: null, pinnedId: null });
  const [sourcingZones, setSourcingZones] = useState([]);
  const [zoneFocusRequest, setZoneFocusRequest] = useState(null);
  const handleRegionZctaStateChange = useCallback((next) => {
    setMapZctaState((current) => (
      current.activeId === next.activeId && current.pinnedId === next.pinnedId ? current : next
    ));
  }, []);
  const [detailsStacked, setDetailsStacked] = useState(false);
  const [collapsed, setCollapsed] = useState(initiallyCollapsed);
  const markets = useMemo(() => rankResearchMarkets(Array.isArray(payload?.markets) ? payload.markets : []), [payload]);
  const filteredMarkets = useMemo(() => filterMarketsByState(
    filterResearchMarkets(markets, marketSearch), selectedState,
  ), [marketSearch, markets, selectedState]);
  const selectedTargetKeys = useMemo(() => new Set(
    selectedSearchTargets.map(searchTargetKey),
  ), [selectedSearchTargets]);
  const regionZctas = useMemo(() => {
    if (!selectedRegion || !regionZctaPayload) return [];
    const geometry = buildRegionZctaGeometry(
      selectedRegion.market_county_fips?.length
        ? selectedRegion.market_county_fips
        : [selectedRegion.marker_county_fips],
      regionZctaPayload,
    );
    return (geometry?.zctas || []).map((shape) => ({
      ...shape.properties,
      zcta5: shape.geoId,
      state_abbr: shape.properties?.state_abbr || selectedRegion.state_name,
    }));
  }, [regionZctaPayload, selectedRegion]);
  const regionZones = useMemo(() => {
    if (!selectedRegion) return [];
    const outline = regionZctaPayload?.region_outline?.geometry;
    return outline
      ? zonesInRegion(sourcingZones, outline)
      : sourcingZones.filter((zone) => zone.state_abbr === selectedRegion.state_name);
  }, [regionZctaPayload, selectedRegion, sourcingZones]);
  const saveZone = async (name, geometry) => {
    const zone = await apiJson('/sourcing-zones', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, state_abbr: selectedRegion.state_name, geometry_geojson: geometry }),
    });
    setSourcingZones((current) => [zone, ...current]);
    return zone;
  };
  const updateZone = async (zoneId, geometry) => {
    const zone = await apiJson(`/sourcing-zones/${zoneId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ geometry_geojson: geometry }),
    });
    setSourcingZones((current) => current.map((item) => (String(item.zone_id) === String(zoneId) ? zone : item)));
    return zone;
  };
  const deleteZone = async (zoneId) => {
    await apiJson(`/sourcing-zones/${zoneId}`, { method: 'DELETE' });
    setSourcingZones((current) => current.filter((item) => String(item.zone_id) !== String(zoneId)));
  };
  const tableSelectedIds = selectedSearchTargets
    .filter((target) => target.type === 'research_market')
    .map((target) => target.id);
  const updateTableMarkets = (marketIds) => {
    if (typeof onSelectedSearchTargetsChange !== 'function') {
      onSelectedMarketIdsChange?.(marketIds);
      return;
    }
    onSelectedSearchTargetsChange([
      ...selectedSearchTargets.filter((target) => target.type !== 'research_market'),
      ...marketIds.map((id) => ({ type: 'research_market', id })),
    ]);
  };
  const toggleMapTarget = (target) => {
    if (typeof onSelectedSearchTargetsChange !== 'function') return;
    const key = searchTargetKey(target);
    onSelectedSearchTargetsChange(selectedTargetKeys.has(key)
      ? selectedSearchTargets.filter((item) => searchTargetKey(item) !== key)
      : [...selectedSearchTargets, target]);
  };
  const changeSelectedState = (stateAbbr) => {
    setSelectedState(stateAbbr);
    if (!selectedRegion || selectedRegion.state_name !== stateAbbr) {
      setSelectedRegion(null);
      setRegionZctaPayload(null);
      setRegionError('');
      setRegionLoading(false);
    }
  };
  const prefetchRegionMap = useCallback((market) => {
    loadRegionGeography(market).catch(() => {});
  }, []);
  const openRegionMap = async (market) => {
    const stateAbbr = String(market?.state_name || '').toUpperCase();
    if (!market?.region_slug || !stateAbbr) return;
    const requestId = regionRequestRef.current + 1;
    regionRequestRef.current = requestId;
    setSelectedRegion(market);
    setTableHoverZcta(null);
    setZctaFocusRequest(null);
    setRegionZctaPayload(null);
    setRegionError('');
    setRegionLoading(true);
    try {
      // Reuse a hover-prefetched/in-flight response or the bounded memory cache.
      const nextPayload = await loadRegionGeography(market);
      if (regionRequestRef.current !== requestId) return;
      setRegionZctaPayload(nextPayload);
      // Saved areas are supplementary; a failure here never blocks the ZIP map.
      apiJson('/sourcing-zones')
        .then((result) => {
          if (regionRequestRef.current === requestId) setSourcingZones(Array.isArray(result?.zones) ? result.zones : []);
        })
        .catch(() => {});
    } catch (requestError) {
      if (regionRequestRef.current !== requestId) return;
      setRegionError(requestError?.message || 'Could not load ZIP-area boundaries for this region.');
    } finally {
      if (regionRequestRef.current === requestId) setRegionLoading(false);
    }
  };
  const closeRegionMap = () => {
    regionRequestRef.current += 1;
    setSelectedRegion(null);
    setRegionZctaPayload(null);
    setRegionError('');
    setRegionLoading(false);
  };

  if (collapsible && collapsed) {
    return <section className="research-regions-collapsed"><div className="fema-screening-map__eyebrow fema-screening-map__live-data"><span className="fema-screening-map__live-dot" aria-hidden="true" /><strong>Live data - {markets.length.toLocaleString()} metros</strong></div><button type="button" onClick={() => setCollapsed(false)} aria-label="Expand U.S. market research"><i className="pi pi-chevron-down" aria-hidden="true" /></button></section>;
  }

  return <section className={`research-regions-workspace${collapsible ? ' is-collapsible' : ''}`}>
    {collapsible ? <button type="button" className="research-regions-collapse" onClick={() => setCollapsed(true)} aria-label="Collapse U.S. market research"><i className="pi pi-chevron-up" aria-hidden="true" /></button> : null}
    <div className={`research-map-table-demo research-map-table-demo--split${detailsStacked ? ' is-stacked' : ''}`}>
      <FemaNriScreeningMapSection active={active} initialPayload={payload} loading={loading} error={error} viewTabs={viewTabs} marketSearch={marketSearch} onMarketSearchChange={setMarketSearch} onSelectedStateChange={changeSelectedState} selectedRegion={selectedRegion} regionZctaPayload={regionZctaPayload} regionLoading={regionLoading} regionError={regionError} onRegionMapOpen={openRegionMap} onRegionMapPrefetch={prefetchRegionMap} onRegionMapBack={closeRegionMap} highlightedZcta={tableHoverZcta} zctaFocusRequest={zctaFocusRequest} onRegionZctaStateChange={handleRegionZctaStateChange} sourcingZones={regionZones} zoneFocusRequest={zoneFocusRequest} onSaveZone={saveZone} onUpdateZone={updateZone} onDeleteZone={deleteZone} showMarketFilter showSourceAudit={showSourceAudit} sourceAuditKeys={sourceAuditKeys} showTitle={showTitle} headerSupplement={headerSupplement} detailsStacked={detailsStacked} onDetailsLayoutToggle={() => setDetailsStacked((stacked) => !stacked)} selectedSearchTargetKeys={selectedTargetKeys} onSearchTargetToggle={toggleMapTarget} onRunSearchTarget={onRunSearchTarget} selectionLimit={selectionLimit} selectedSearchTargetCount={selectedSearchTargets.length} getMarketSelectionId={getMarketSelectionId} isMarketSelectable={isMarketSelectable} selectionPurpose={selectionPurpose} />
      {!loading && !error ? selectedRegion ? (
        regionLoading || regionError ? <div className="research-map-index research-map-index--split research-map-index--status"><strong>{regionLoading ? `Loading ${selectedRegion.region_name} ZIP areas` : 'ZIP areas unavailable'}</strong><span>{regionError || 'Keeping the current map visible while the region geography loads.'}</span></div> : <CompactZctaIndex zctas={regionZctas} selectedTargetKeys={selectedTargetKeys} onTargetToggle={onSelectedSearchTargetsChange ? toggleMapTarget : undefined} selectionLimit={selectionLimit} selectedTargetCount={selectedSearchTargets.length} selectionPurpose={selectionPurpose} highlightedZcta={mapZctaState.activeId} pinnedZcta={mapZctaState.pinnedId} onRowHover={setTableHoverZcta} onRowFocus={(zcta5) => setZctaFocusRequest({ zcta5, nonce: Date.now() })} zones={regionZones} onZoneFocus={(zoneId) => setZoneFocusRequest({ zoneId, nonce: Date.now() })} />
      ) : <CompactMarketIndex markets={filteredMarkets} selectedMarketIds={onSelectedSearchTargetsChange ? tableSelectedIds : selectedMarketIds} onSelectedMarketIdsChange={onSelectedSearchTargetsChange ? updateTableMarkets : onSelectedMarketIdsChange} selectionLimit={Math.max(0, selectionLimit - selectedSearchTargets.filter((target) => target.type !== 'research_market').length)} getMarketSelectionId={getMarketSelectionId} isMarketSelectable={isMarketSelectable} getMarketDisabledReason={getMarketDisabledReason} selectionPurpose={selectionPurpose} /> : null}
    </div>
  </section>;
}
